import React from 'react';
import { useParams } from 'react-router-dom';

const FEATURE_DEFS = {
  'split-videos': {
    icon: '✂️',
    title: 'Split Longer Videos',
    description:
      'Split longer videos into smaller parts based on your selected time or size. This will make it easier to process long recordings and keep file sizes manageable.',
    capabilities: [
      'Split by time (e.g. every X minutes)',
      'Split by target size (MB)',
      'Keep original video quality as much as possible',
      'Output parts ready for extraction or download'
    ],
    useCases: [
      'Long lectures, meetings, podcasts, or streams that need to be processed in smaller chunks',
      'Uploading to platforms with file size or duration limits',
      'Preparing multi-part content for faster scene extraction and easier editing',
      'Archiving recordings into manageable segments'
    ]
  },
  'auto-merge': {
    icon: '🧩',
    title: 'Automatic Video Merge',
    description:
      'Automatically merge your uploaded clips into one clean final video. Ideal for multi-part recordings and sequences that belong together.',
    capabilities: [
      'Merge clips in the correct order',
      'Support common video formats',
      'Produce a single downloadable output file',
      'Optimized for reliable processing on the server'
    ],
    useCases: [
      'Combining multiple camera clips into a single final render',
      'Merging recordings from segmented uploads (part 1, part 2, ...)',
      'Creating compilations and highlight reels from separate clips',
      'Preparing a single file before running extraction'
    ]
  }
};

const ComingSoonFeature = () => {
  const { featureId } = useParams();
  const feature = FEATURE_DEFS[featureId];

  const isSplitVideos = featureId === 'split-videos';

  if (!feature) {
    return (
      <div style={{ padding: '0.5rem' }}>
        <div className="bg-slate-700/50 rounded-xl p-8 border border-slate-600/50">
          <h2 className="text-2xl font-bold text-white mb-2">Coming Soon</h2>
          <p className="text-gray-300">This feature is not available yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0.5rem' }}>
      <div className="bg-slate-700/50 rounded-xl p-8 border border-slate-600/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="text-3xl">{feature.icon}</div>
          <h2 className="text-2xl font-bold text-white">{feature.title}</h2>
        </div>

        <p className="text-gray-300 mb-6">{feature.description}</p>

        {isSplitVideos ? (
          <div className="my-10 flex justify-center">
            <div className="px-6 py-4 rounded-xl bg-blue-500/15 border border-blue-500/40 text-blue-100 font-semibold tracking-wide">
              Stay tuned!
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-3">
              What it will do
            </h3>
            <ul className="space-y-2 mb-6">
              {feature.capabilities.map((item) => (
                <li key={item} className="text-gray-300 flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-3">
              Use cases
            </h3>
            <ul className="space-y-2">
              {feature.useCases.map((item) => (
                <li key={item} className="text-gray-300 flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {!isSplitVideos && (
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-200">
              <span className="mr-2">⭐</span>
              Premium tier is coming soon. Stay tuned!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComingSoonFeature;
